// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Star, Zap, Crown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { usePurchaseMembership } from '@/hooks/usePurchaseMembership';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { MembershipCouponInput } from '@/components/checkout/MembershipCouponInput';
import { PaymentPlanSelector } from '@/components/checkout/PaymentPlanSelector';
import { CouponValidationResult } from '@/types/affiliateCoupons';

interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string;
  price_cents: number;
  billing_type: string;
  is_addon: boolean;
  features: string[];
}

interface AccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  upgradeMessage?: string;
  highlightPlan?: string;
}

const AccountModal: React.FC<AccountModalProps> = ({ 
  open, 
  onOpenChange, 
  upgradeMessage,
  highlightPlan: propHighlightPlan 
}) => {
  const { user, profile, refreshProfile } = useAuth();
  const { purchaseMembership, isProcessing, handleStripeSuccess } = usePurchaseMembership();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidationResult | null>(null);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState<string | null>(null);
  const [selectedPaymentPrice, setSelectedPaymentPrice] = useState<number | null>(null);
  const [selectedBillingType, setSelectedBillingType] = useState<string>('one_time');
  const location = useLocation();
  const navigate = useNavigate();

  // Check for Stripe success/cancel on modal open
  useEffect(() => {
    if (open) {
      const searchParams = new URLSearchParams(location.search);
      const checkoutStatus = searchParams.get('checkout');
      const sessionId = searchParams.get('session_id');

      console.log('🔍 [ACCOUNT-MODAL] Checking URL params:', {
        checkoutStatus,
        sessionId,
        fullSearch: location.search,
        timestamp: new Date().toISOString()
      });

      if (checkoutStatus === 'success' && sessionId) {
        console.log('✅ [ACCOUNT-MODAL] Stripe checkout success detected, processing...');
        handleStripeSuccess(sessionId).then(() => {
          // Clear URL params after processing
          const newUrl = location.pathname;
          navigate(newUrl, { replace: true });
          toast.success('Membership upgraded successfully!');
        }).catch((error) => {
          console.error('❌ [ACCOUNT-MODAL] Error processing Stripe success:', error);
          toast.error('Failed to update membership. Please contact support.');
        });
      } else if (checkoutStatus === 'canceled') {
        console.log('❌ [ACCOUNT-MODAL] Stripe checkout canceled');
        toast.info('Checkout was canceled');
        // Clear URL params
        const newUrl = location.pathname;
        navigate(newUrl, { replace: true });
      }
    }
  }, [open, location.search, handleStripeSuccess, navigate, location.pathname]);

  // Fallback hardcoded plans in case dynamic loading fails
  const fallbackPlans: Plan[] = [
    {
      id: 'free',
      name: 'Free',
      slug: 'free',
      description: 'Basic features to get started',
      price_cents: 0,
      billing_type: 'free',
      is_addon: false,
      features: [
        'Basic profile creation',
        'Limited college search',
        'Basic recruiting tracker'
      ]
    },
    {
      id: 'athlete_premium',
      name: 'Premium',
      slug: 'athlete_premium',
      description: 'Enhanced features for serious athletes',
      price_cents: 2999,
      billing_type: 'monthly',
      is_addon: false,
      features: [
        'Full profile customization',
        'Advanced college search',
        'Complete recruiting tools',
        'Premium support'
      ]
    },
    {
      id: 'athlete_elite',
      name: 'Elite',
      slug: 'athlete_elite',
      description: 'All features for elite athletes',
      price_cents: 4999,
      billing_type: 'monthly',
      is_addon: false,
      features: [
        'Everything in Premium',
        'Personal recruiting advisor',
        'Elite showcases',
        'Priority support'
      ]
    }
  ];

  // Load plans from Supabase
  useEffect(() => {
    const loadPlans = async () => {
      console.log('🔄 [ACCOUNT-MODAL] Starting to load membership plans from Supabase...');
      setIsLoadingPlans(true);
      
      try {
        const { data: memberships, error } = await supabase
          .from('memberships')
          .select('id, name, slug, description, price_cents, billing_type, is_addon, is_active')
          .eq('role_slug', 'athlete')
          .eq('is_active', true)
          .order('price_cents', { ascending: true });

        console.log('📊 [ACCOUNT-MODAL] Supabase query results:', { memberships, error });

        if (error) {
          console.error('❌ [ACCOUNT-MODAL] Error loading plans from Supabase:', error);
          console.log('🔄 [ACCOUNT-MODAL] Falling back to hardcoded plans');
          setPlans(fallbackPlans);
        } else if (memberships && memberships.length > 0) {
          console.log(`✅ [ACCOUNT-MODAL] Successfully loaded ${memberships.length} plans from Supabase`);
          
          const formattedPlans: Plan[] = memberships.map(membership => ({
            id: membership.id,
            name: membership.name,
            slug: membership.slug,
            description: membership.description || '',
            price_cents: membership.price_cents || 0,
            billing_type: membership.billing_type || 'monthly',
            is_addon: membership.is_addon || false,
            features: getFeaturesByPlan(membership.slug)
          }));
          
          console.log('📋 [ACCOUNT-MODAL] Formatted plans:', formattedPlans);
          setPlans(formattedPlans);
        } else {
          console.log('⚠️ [ACCOUNT-MODAL] No active athlete memberships found, using fallback plans');
          setPlans(fallbackPlans);
        }
      } catch (error) {
        console.error('💥 [ACCOUNT-MODAL] Exception while loading plans:', error);
        console.log('🔄 [ACCOUNT-MODAL] Using fallback plans due to exception');
        setPlans(fallbackPlans);
      } finally {
        setIsLoadingPlans(false);
        console.log('✅ [ACCOUNT-MODAL] Plan loading completed');
      }
    };

    if (open) {
      loadPlans();
    }
  }, [open]);

  // Helper function to get features by plan
  const getFeaturesByPlan = (slug: string): string[] => {
    const featureMap: { [key: string]: string[] } = {
      'free': [
        'Basic profile creation',
        'Limited college search',
        'Basic recruiting tracker'
      ],
      'athlete_premium': [
        'Full profile customization',
        'Advanced college search',
        'Complete recruiting tools',
        'Premium support'
      ],
      'athlete_elite': [
        'Everything in Premium',
        'Personal recruiting advisor',
        'Elite showcases',
        'Priority support'
      ]
    };
    return featureMap[slug] || ['Standard features'];
  };

  const formatPrice = (price_cents: number, billing_type: string): string => {
    if (price_cents === 0) return 'Free';
    
    const dollars = (price_cents / 100).toFixed(2);
    if (billing_type === 'monthly') {
      return `$${dollars}/month`;
    } else if (billing_type === 'annual') {
      return `$${dollars}/year`;
    } else {
      return `$${dollars} one-time`;
    }
  };

  const currentPlan = profile?.active_membership_slug || 'free';
  const highlightPlan = propHighlightPlan || 'athlete_premium';

  console.log('🔍 [ACCOUNT-MODAL] Current modal state:', {
    isOpen: open,
    currentPlan,
    highlightPlan,
    selectedPlan,
    isLoadingPlans,
    plansCount: plans.length,
    profileId: profile?.id,
    userId: user?.id,
    upgradeMessage
  });

  const handleUpgrade = async (plan: Plan) => {
    console.log('🚀 [ACCOUNT-MODAL] Upgrade button clicked:', {
      planSlug: plan.slug,
      planName: plan.name,
      currentPlan,
      priceCents: plan.price_cents,
      isCurrentPlan: currentPlan === plan.slug,
      timestamp: new Date().toISOString()
    });

    if (currentPlan === plan.slug) {
      console.log('ℹ️ [ACCOUNT-MODAL] User already has this plan, showing info message');
      toast.info(`You already have the ${plan.name} plan`);
      return;
    }

    if (!user || !profile) {
      console.error('❌ [ACCOUNT-MODAL] No user or profile available for subscription');
      toast.error('Please log in to upgrade your membership');
      return;
    }

    setSelectedPlan(plan.slug);
    console.log('🎯 [ACCOUNT-MODAL] Selected plan set to:', plan.slug);

    // For free plan, just update directly
    if (plan.slug === 'free' || plan.price_cents === 0) {
      console.log('🆓 [ACCOUNT-MODAL] Processing free plan upgrade');
      
      try {
        // Membership columns on profiles are server-owned; the database trigger
        // ignores client writes, so we no longer attempt one here.
        console.log('🆓 [ACCOUNT-MODAL] Free plan selected — no client-side membership write');

        
        // Refresh the profile to get updated data
        console.log('🔄 [ACCOUNT-MODAL] Refreshing profile after free plan update...');
        await refreshProfile();
        
        toast.success(`Successfully switched to ${plan.name} plan!`);
        onOpenChange(false);
        
      } catch (error) {
        console.error('💥 [ACCOUNT-MODAL] Exception during free plan upgrade:', error);
        toast.error('Failed to update membership. Please try again.');
      } finally {
        setSelectedPlan(null);
      }
    } else {
      // For paid plans, redirect to Stripe
      console.log('💰 [ACCOUNT-MODAL] Paid plan detected, redirecting to Stripe...');
      try {
        // Determine final price based on payment plan selection
        let finalPriceCents = plan.price_cents;
        let billingType = plan.billing_type;
        
        // If payment plan is selected, use payment plan pricing
        if (selectedPaymentPlan && selectedPaymentPrice) {
          finalPriceCents = selectedPaymentPrice;
          billingType = selectedBillingType;
          console.log('📋 [ACCOUNT-MODAL] Using payment plan pricing:', {
            paymentPlanId: selectedPaymentPlan,
            price: finalPriceCents,
            billingType
          });
        }
        // Note: We no longer send discounted price_cents. 
        // The checkout function will handle coupon validation and apply it in Stripe

        await purchaseMembership({
          membership_slug: plan.slug,
          billing_type: billingType,
          price_cents: finalPriceCents, // Always send original price (unless payment plan)
          audience_type: 'athlete',
          source: 'account_modal',
          coupon_code: appliedCoupon?.coupon?.code || null,
          payment_plan_id: selectedPaymentPlan || undefined
        });
      } catch (error) {
        console.error('💥 [ACCOUNT-MODAL] Error in Stripe subscription process:', error);
        setSelectedPlan(null);
      }
    }
  };

  if (isLoadingPlans) {
    console.log('⏳ [ACCOUNT-MODAL] Showing loading state...');
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full sm:max-w-2xl lg:max-w-5xl xl:max-w-6xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Account & Billing</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-6 sm:p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Loading membership plans...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  console.log('🖼️ [ACCOUNT-MODAL] Rendering modal with plans:', {
    plansCount: plans.length,
    currentPlan,
    highlightPlan,
    selectedPlan
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full sm:max-w-2xl lg:max-w-5xl xl:max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Account & Billing</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 sm:space-y-6">
          {/* Show upgrade message if provided */}
          {upgradeMessage && (
            <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
              <p className="text-sm text-blue-800">{upgradeMessage}</p>
            </div>
          )}

          {/* Current Plan Info */}
          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Current Plan</h3>
            <p className="text-sm text-gray-600">
              You are currently on the <span className="font-medium">{currentPlan}</span> plan.
            </p>
          </div>

          {/* Plans List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plans.map((plan) => {
              const isCurrentPlan = currentPlan === plan.slug;
              const isHighlighted = highlightPlan === plan.slug;
              const isSelected = selectedPlan === plan.slug;

              console.log(`🎨 [ACCOUNT-MODAL] Rendering plan card for ${plan.slug}:`, {
                isCurrentPlan,
                isHighlighted,
                isSelected,
                priceCents: plan.price_cents,
                billingType: plan.billing_type
              });

              return (
                <Card 
                  key={plan.id} 
                  className={`relative ${
                    isCurrentPlan ? 'border-2 border-green-500 shadow-lg' :
                    isHighlighted ? 'border-2 border-blue-500 shadow-lg' :
                    'border border-gray-200'
                  } flex flex-col`}
                >
                  {isHighlighted && !isCurrentPlan && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-blue-500 text-white">
                        <Star className="w-3 h-3 mr-1" />
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  
                  {isCurrentPlan && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-green-500 text-white">
                        <Check className="w-3 h-3 mr-1" />
                        Current Plan
                      </Badge>
                    </div>
                  )}

                  <CardHeader className="sm:flex-1 text-center sm:text-left sm:p-6">
                    <CardTitle className="flex items-center justify-center sm:justify-start gap-2">
                      {plan.name}
                      {plan.slug === 'athlete_elite' && <Crown className="w-5 h-5 text-yellow-500" />}
                      {plan.slug === 'athlete_premium' && <Zap className="w-5 h-5 text-blue-500" />}
                    </CardTitle>
                    <div className="text-2xl sm:text-3xl font-bold">
                      {appliedCoupon?.valid && appliedCoupon?.final_price !== undefined ? (
                        <div className="space-y-1">
                          <div className="line-through text-lg sm:text-xl text-muted-foreground">
                            {formatPrice(plan.price_cents, plan.billing_type)}
                          </div>
                          <div className="text-green-600">
                            {formatPrice(appliedCoupon.final_price, plan.billing_type)}
                          </div>
                        </div>
                      ) : (
                        formatPrice(plan.price_cents, plan.billing_type)
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{plan.description}</p>
                  </CardHeader>

                  <CardContent className="space-y-4 sm:flex-1">
                    <ul className="space-y-2">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleUpgrade(plan)}
                      disabled={isProcessing || isSelected}
                      variant={
                        isCurrentPlan ? 'success' :
                        plan.slug === 'athlete_elite' ? 'default' :
                        plan.slug === 'athlete_premium' ? 'default' :
                        plan.price_cents === 0 ? 'secondary' :
                        'default'
                      }
                      className="w-full"
                    >
                      {isSelected && isProcessing ? 'Processing...' :
                       isCurrentPlan ? 'Current Plan' :
                       plan.price_cents === 0 ? 'Switch to Free' :
                       'Upgrade Now'}
                    </Button>

                    {/* Payment Plan Selector for High-Value Plans */}
                    {plan.price_cents >= 10000 && !isCurrentPlan && (
                      <PaymentPlanSelector
                        membershipId={plan.id}
                        membershipName={plan.name}
                        originalPrice={plan.price_cents}
                        onPlanSelect={(planId, priceCents, billingType, paymentPlanId) => {
                          setSelectedPaymentPlan(planId);
                          setSelectedPaymentPrice(priceCents);
                          setSelectedBillingType(billingType);
                        }}
                        selectedPlanId={selectedPaymentPlan}
                      />
                    )}

                    {/* Add Coupon Input for Paid Plans */}
                    {plan.price_cents > 0 && !isCurrentPlan && (
                      <div className="mt-4 pt-4 border-t">
                        <MembershipCouponInput
                          membershipSlug={plan.slug}
                          priceCents={selectedPaymentPrice || plan.price_cents}
                          onCouponValidated={setAppliedCoupon}
                          onCouponRemoved={() => setAppliedCoupon(null)}
                          disabled={isProcessing || isSelected}
                          isOneTimePayment={plan.billing_type === 'one_time' || plan.price_cents >= 10000}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountModal;